import { Subscription } from '@rails/actioncable';
import {
  getV1CompaniesCompanyIdProjectsProjectIdProjectEvents,
  GetV1CompaniesCompanyIdProjectsProjectIdProjectEventsData,
  ProjectEvent_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import fetchAllPages, { parseApiPage } from '~utils/fetchAllPages';
import { channelMessageData } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ProjectEvent } from './projectEventModels';

type GetProjectEventsData = GetV1CompaniesCompanyIdProjectsProjectIdProjectEventsData;

type ProjectEventChannelMessage = { data: ProjectEvent_Read };

export default function useProjectEvents() {
  const { userStore } = useStores();

  const getAllProjectEvents = useCallback(
    (
      projectId: GetProjectEventsData['path']['project-id'],
      companyId: GetProjectEventsData['path']['company-id'],
      signal?: AbortSignal,
    ) =>
      fetchAllPages(
        async (cursor, limit, pageSignal) => {
          try {
            const response = await getV1CompaniesCompanyIdProjectsProjectIdProjectEvents({
              path: { 'project-id': projectId, 'company-id': companyId },
              query: {
                'page[limit]': limit,
                ...(cursor ? { 'page[after]': cursor } : {}),
              },
              signal: pageSignal,
            });

            return parseApiPage(response, ProjectEvent.parse);
          } catch (error) {
            if (pageSignal?.aborted) {
              throw error;
            }

            reportRequestError(
              error,
              t('error_messages.projects.failed_to_fetch_project_events'),
            );

            return undefined;
          }
        },
        { signal },
      ),
    [],
  );

  const subscribeToProjectEventUpdates = useCallback(
    ({
      projectId,
      onMessageCallBack,
    }: {
      projectId: string;
      onMessageCallBack: (event: ProjectEvent) => void;
    }): Subscription | undefined => {
      const consumer = connection.realTimeConnection;
      const channel = realTimeChannels.CompanyProjectEventChannel;
      const companyId = getEffectiveUserCompanyId(userStore);

      let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
        const identifier = sub.identifier;
        return identifier.includes(channel) && identifier.includes(projectId);
      });

      if (!subscription) {
        subscription = consumer?.subscriptions?.create?.(
          {
            channel,
            company_id: companyId,
            project_id: projectId,
          },
          {
            connected: () => () => {},
            disconnected: () => () => {},
            received: (message: unknown) => {
              const data =
                channelMessageData<ProjectEventChannelMessage['data']>(message);
              if (!data) return;

              onMessageCallBack(ProjectEvent.parse(data));
            },
          },
        );
      }

      return subscription;
    },
    [userStore],
  );

  return {
    getAllProjectEvents,
    subscribeToProjectEventUpdates,
  };
}
